import { C, DEMO_DURATION, background, banner, glow, lerp, smooth, text } from "@/components/demos/canvasKit";

// Hook keyframes: [phase, x key, y key]. The crane and hook are a standard port crane; only the net is new.
const KEYS = [
  [0.0, "shipOff", "up"],
  [0.12, "shipOff", "sweep"],
  [0.22, "ship", "sweep"],
  [0.26, "ship", "catch"],
  [0.38, "ship", "up"],
  [0.56, "quay", "up"],
  [0.68, "quay", "land"],
  [0.74, "quay", "slack"],
  [0.82, "quayOff", "slack"],
  [0.9, "quayOff", "up"],
  [1.0, "shipOff", "up"],
];
const BANNERS = [
  [0.12, "EYE HELD UP AND OPEN · READY FOR ANY HOOK", "EYE HELD UP AND OPEN"],
  [0.26, "THE HOOK SWEEPS THROUGH THE EYE", "HOOK SWEEPS THROUGH"],
  [0.4, "CAUGHT AND LIFTED · NOBODY ON DECK", "LIFTED · NOBODY ON DECK"],
  [0.68, "SAME CRANE · SAME HOOK · SAME PORT", "SAME CRANE · SAME HOOK"],
  [0.88, "ASHORE · UNHOOKED AS USUAL", "ASHORE · AS USUAL"],
  [1.01, "NEXT PALLET", "NEXT PALLET"],
];

const geom = (w, h) => {
  const deck = h * 0.6;
  const quayTop = h * 0.7;
  const jibY = h * 0.15;
  const pw = Math.max(34, w * 0.085);
  const pH = Math.max(20, h * 0.08);
  const stalk = Math.max(14, h * 0.06);
  const r = Math.max(6, Math.min(w, h) * 0.022);
  const hr = r * 0.85;
  const eyeX = w * 0.38;
  const qx = w * 0.76;
  const eyeCY = deck - pH - stalk - r;
  const landCY = quayTop - pH - stalk - r;
  const off = (r + hr) * 2.4;
  const hang = hr + r;
  return {
    deck, quayTop, jibY, pw, pH, stalk, r, hr, eyeX, qx,
    hx: { ship: eyeX + hr, shipOff: eyeX + hr + off, quay: qx + hr, quayOff: qx + hr + off },
    hy: { up: Math.max(h * 0.26, jibY + 34), catch: eyeCY - hang, sweep: eyeCY - hang + r * 0.8, land: landCY - hang, slack: landCY - hang + r * 0.8 },
  };
};

const hookAt = (ph, g) => {
  let i = 0;
  while (i < KEYS.length - 2 && ph >= KEYS[i + 1][0]) i++;
  const [a, ax, ay] = KEYS[i];
  const [b, bx, by] = KEYS[i + 1];
  const k = smooth(a, b, ph);
  return { x: lerp(g.hx[ax], g.hx[bx], k), y: lerp(g.hy[ay], g.hy[by], k) };
};

const drawScene = (ctx, w, h, g, narrow) => {
  ctx.fillStyle = "rgba(14,26,38,0.85)";
  ctx.fillRect(0, h * 0.8, w, h * 0.2);
  ctx.fillStyle = "#121a24";
  ctx.strokeStyle = C.landEdge;
  ctx.beginPath();
  ctx.moveTo(0.02 * w, g.deck);
  ctx.lineTo(0.52 * w, g.deck);
  ctx.lineTo(0.49 * w, h * 0.9);
  ctx.lineTo(0.05 * w, h * 0.9);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  text(ctx, "NOBODY ON DECK", 0.27 * w, g.deck + h * 0.12, { color: C.fg3, size: 10.5, align: "center" });
  ctx.fillStyle = "#151e29";
  ctx.fillRect(0.6 * w, g.quayTop, 0.4 * w, h - g.quayTop);
  ctx.strokeRect(0.6 * w, g.quayTop, 0.4 * w, h - g.quayTop);
  const tx = w * 0.93;
  const top = g.jibY - h * 0.08;
  ctx.strokeStyle = "#3a4a5c";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(tx, g.quayTop);
  ctx.lineTo(tx, top);
  ctx.moveTo(w * 0.1, g.jibY);
  ctx.lineTo(w * 0.985, g.jibY);
  ctx.moveTo(tx, top);
  ctx.lineTo(w * 0.1, g.jibY);
  ctx.moveTo(tx, top);
  ctx.lineTo(w * 0.985, g.jibY);
  ctx.stroke();
  ctx.lineWidth = 1;
  ctx.fillStyle = "#2f3d4d";
  ctx.fillRect(tx - 9, g.jibY + 4, 14, 12);
  text(ctx, narrow ? "CRANE · AS IS" : "PORT CRANE & HOOK · AS IS", tx - 12, g.quayTop + 18, { color: C.fg2, size: 10.5, align: "right" });
};

const drawPallet = (ctx, cx, top, g, alpha = 1) => {
  const { pw, pH, stalk, r } = g;
  const x0 = cx - pw / 2;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = "#1c2a36";
  ctx.fillRect(x0, top, pw, pH);
  ctx.save();
  ctx.beginPath();
  ctx.rect(x0, top, pw, pH);
  ctx.clip();
  ctx.strokeStyle = "rgba(0,242,254,0.32)";
  ctx.beginPath();
  for (let x = -pH; x < pw + pH; x += 9) {
    ctx.moveTo(x0 + x, top);
    ctx.lineTo(x0 + x + pH, top + pH);
    ctx.moveTo(x0 + x + pH, top);
    ctx.lineTo(x0 + x, top + pH);
  }
  ctx.stroke();
  ctx.restore();
  ctx.strokeStyle = C.landEdge;
  ctx.strokeRect(x0, top, pw, pH);
  const gy = top - stalk * 0.3;
  ctx.strokeStyle = "rgba(0,242,254,0.6)";
  ctx.beginPath();
  [-0.5, -0.17, 0.17, 0.5].forEach((f) => {
    ctx.moveTo(cx + f * pw, top);
    ctx.lineTo(cx, gy);
  });
  ctx.stroke();
  const eyeCY = top - stalk - r;
  ctx.strokeStyle = C.cyan;
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(cx, gy);
  ctx.lineTo(cx, eyeCY + r);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(cx, eyeCY, r * 0.62, r, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
  return eyeCY;
};

const drawHook = (ctx, x, y, g) => {
  const { hr, jibY } = g;
  ctx.fillStyle = "#2f3d4d";
  ctx.fillRect(x - 10, jibY - 3, 20, 8);
  ctx.strokeStyle = "#7b8a99";
  ctx.beginPath();
  ctx.moveTo(x - 3, jibY + 5);
  ctx.lineTo(x - 3, y - 22);
  ctx.moveTo(x + 3, jibY + 5);
  ctx.lineTo(x + 3, y - 22);
  ctx.stroke();
  ctx.fillStyle = "#c9d1d9";
  ctx.fillRect(x - 8, y - 24, 16, 9);
  ctx.strokeStyle = "#c9d1d9";
  ctx.lineWidth = 2.6;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(x, y - 15);
  ctx.lineTo(x, y);
  ctx.arc(x - hr, y, hr, 0, Math.PI, false);
  ctx.lineTo(x - 2 * hr, y - hr * 0.7);
  ctx.stroke();
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(x, y - 5);
  ctx.lineTo(x - 2 * hr + 1, y - hr * 0.7);
  ctx.stroke();
  ctx.lineCap = "butt";
  ctx.lineWidth = 1;
};

const drawPerson = (ctx, x, y, alpha) => {
  if (alpha <= 0.01) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = C.fg2;
  ctx.fillStyle = C.fg2;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.arc(x, y - 19, 3.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(x, y - 15);
  ctx.lineTo(x, y - 7);
  ctx.lineTo(x - 3, y);
  ctx.moveTo(x, y - 7);
  ctx.lineTo(x + 3, y);
  ctx.moveTo(x, y - 13);
  ctx.lineTo(x - 6, y - 17);
  ctx.stroke();
  ctx.restore();
};

export function drawNet(ctx, w, h, t) {
  background(ctx, w, h);
  const narrow = w < 560;
  const g = geom(w, h);
  const ph = (t % DEMO_DURATION) / DEMO_DURATION;
  drawScene(ctx, w, h, g, narrow);
  [0.12, 0.25].forEach((f) => drawPallet(ctx, w * f, g.deck - g.pH, g));
  const hk = hookAt(ph, g);
  if (ph < 0.26) {
    const eyeCY = drawPallet(ctx, g.eyeX, g.deck - g.pH, g, smooth(0, 0.05, ph));
    glow(ctx, g.eyeX, eyeCY, 26, "rgba(0,242,254,0.4)", 0.6 + 0.4 * Math.sin(t * 5));
    text(ctx, "x1025 NET", g.eyeX, eyeCY - g.r - 14, { color: C.cyan, size: 10.5, align: "center" });
  } else if (ph < 0.68) {
    drawPallet(ctx, hk.x - g.hr, hk.y + g.hr + 2 * g.r + g.stalk, g);
  } else {
    drawPallet(ctx, g.qx, g.quayTop - g.pH, g, 1 - smooth(0.94, 1, ph));
  }
  drawHook(ctx, hk.x, hk.y, g);
  drawPerson(ctx, g.qx + w * 0.075, g.quayTop, smooth(0.62, 0.7, ph) * (1 - smooth(0.88, 0.94, ph)));
  const b = BANNERS.find(([end]) => ph < end) || BANNERS[BANNERS.length - 1];
  banner(ctx, w, 10, narrow ? b[2] : b[1], C.cyan);
}
